import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { map } from 'rxjs/operators';
import {
    AddCertifications,
    CreateCertificationSuccess,
    DeleteCertification,
    DeselectSelectedCertifications,
    FetchCertifications,
    FetchCertificationsFailure,
    FetchCertificationsSuccess,
    MoveCertification,
    SearchCertifications,
    SelectFilteredCertifications,
    SetCertifications,
    ToggleSelectedCertification,
    UnsetCertifications,
} from '../actions';
import { CertificationApiService } from '../services/certification.api-service';
import { Certification } from './certification';
import { CertificationState } from './certification.state';
import { CertificationsStateModel } from './certifications.state-model';

@State<CertificationsStateModel>({
    name: 'certifications',
    defaults: new CertificationsStateModel(),
})
@Injectable()
export class CertificationsState extends SubentityState<Certification> {
    @Selector([CertificationState.getEntities])
    static getFilteredEntities(state: CertificationsStateModel, tags: Certification[]): Certification[] {
        if (!tags) {
            return null;
        }

        return tags
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, Certification], b: [number, Certification]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, Certification]) => x[1]);
    }

    @Selector()
    static getSelectedEntityIds(state: CertificationsStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getSearchTerm(state: CertificationsStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: CertificationsStateModel): number {
        return state.totalCount;
    }

    constructor(private store: Store, private _certificationService: CertificationApiService) {
        super(null, 0);
    }

    @Action(AddCertifications)
    addEntities(ctx: StateContext<CertificationsStateModel>, action: AddCertifications): void {
        const tags = this.store.selectSnapshot(CertificationState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map((entity) => entity.id) } }, tags);
    }

    @Action(CreateCertificationSuccess)
    createEntity(ctx: StateContext<CertificationsStateModel>, action: CreateCertificationSuccess): void {
        const tags = this.store.selectSnapshot(CertificationState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: [action.payload.id] } }, tags);
    }

    @Action(DeleteCertification)
    deleteEntity({ getState, patchState }: StateContext<CertificationsStateModel>, { payload }: DeleteCertification): void {
        const state = getState();

        patchState({
            list: [...state.list.filter((subentityId) => subentityId !== payload)],
            selected: [...state.selected.filter((subentityId) => subentityId !== payload)],
            filtered: [...state.filtered.filter((subentityId) => subentityId !== payload)],
        });
    }

    @Action(MoveCertification)
    moveCertification({ getState, patchState }: StateContext<CertificationsStateModel>, { payload }: MoveCertification): void {
        const state = getState();

        patchState({
            list: [...state.list.filter((subentityId) => subentityId !== payload.certificationId)],
            selected: [...state.selected.filter((subentityId) => subentityId !== payload.certificationId)],
            filtered: [...state.filtered.filter((subentityId) => subentityId !== payload.certificationId)],
        });
    }

    @Action(FetchCertifications)
    fetchAll(ctx: StateContext<CertificationsStateModel>, action: FetchCertifications): void {
        ctx.patchState({
            pageSize: action.payload.pageSize,
            pageNumber: action.payload.pageNumber,
        });

        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetCertifications,
            FetchCertificationsSuccess,
            FetchCertificationsFailure,
            this._certificationService
                .getCertifications(action.payload.pageSize, action.payload.pageNumber, action.payload.search, action.payload.provider, action.payload.statuses)
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                        });

                        return data.data.map((query) => new Certification(query));
                    })
                )
        );
    }

    @Action(FetchCertificationsSuccess)
    fetchAllSuccess(ctx: StateContext<CertificationsStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchCertificationsFailure)
    fetchAllFailure(ctx: StateContext<CertificationsStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(SetCertifications)
    setEntities(ctx: StateContext<CertificationsStateModel>, action: SetCertifications): void {
        this._setSubentities(ctx, action, null);
    }

    @Action(UnsetCertifications)
    unsetAll(ctx: StateContext<CertificationsStateModel>): void {
        this._unsetSubentities(ctx);
    }

    @Action(ToggleSelectedCertification)
    toggleSelect(ctx: StateContext<CertificationsStateModel>, action: ToggleSelectedCertification): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredCertifications)
    selectAll(ctx: StateContext<CertificationsStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedCertifications)
    deselectAll(ctx: StateContext<CertificationsStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchCertifications)
    search(ctx: StateContext<CertificationsStateModel>, action: SearchCertifications): void {
        const tags = this.store.selectSnapshot(CertificationState.getEntities);

        this._searchSubentities(ctx, action, tags);
    }
}
